import { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";
import api from "../../lib/api";

const gbp = (x) => `£${(Number(x) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MonthlyTab() {
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  }, []);

  if (invoices === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="monthly-loading-spinner" />
      </div>
    );
  }

  const months = invoices.reduce((acc, inv) => {
    const src = inv.issue_date || (inv.created_at || "").slice(0, 10);
    if (!src) return acc;
    const key = src.slice(0, 7);
    acc[key] = acc[key] || { invoiced: 0, paid: 0, outstanding: 0, count: 0 };
    acc[key].invoiced += inv.total || 0;
    acc[key].count += 1;
    if (inv.status === "paid") acc[key].paid += inv.total || 0;
    if (["sent", "overdue"].includes(inv.status) && (inv.doc_type || "invoice") === "invoice") acc[key].outstanding += inv.total || 0;
    return acc;
  }, {});

  const rows = Object.keys(months).sort((a, b) => (a < b ? 1 : -1));
  const max = Math.max(1, ...rows.map((k) => months[k].invoiced));

  return (
    <div data-testid="monthly-tab">
      <p className="text-sm text-white/50 mb-6">Invoiced value broken down by month</p>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="monthly-empty">
          <TrendingUp className="h-10 w-10 mx-auto text-white/30" />
          <p className="mt-4">No invoices yet.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="monthly-rows">
          {rows.map((k) => {
            const m = months[k];
            const label = new Date(k + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" });
            return (
              <div key={k} className="rounded-lg border border-brand-line bg-brand-surface p-5" data-testid={`monthly-row-${k}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-display font-bold text-white">{label}</p>
                  <p className="text-xs text-white/40">{m.count} document{m.count === 1 ? "" : "s"}</p>
                </div>
                <div className="mt-3 h-2.5 rounded-full bg-brand-obsidian overflow-hidden">
                  <div className="h-full bg-brand-crimson rounded-full" style={{ width: `${(m.invoiced / max) * 100}%` }} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="font-eyebrow text-[9px] uppercase tracking-[0.2em] text-white/40 font-bold">Invoiced</p>
                    <p className="text-sm font-bold text-white" data-testid={`monthly-invoiced-${k}`}>{gbp(m.invoiced)}</p>
                  </div>
                  <div>
                    <p className="font-eyebrow text-[9px] uppercase tracking-[0.2em] text-emerald-400 font-bold">Paid</p>
                    <p className="text-sm font-bold text-emerald-400" data-testid={`monthly-paid-${k}`}>{gbp(m.paid)}</p>
                  </div>
                  <div>
                    <p className="font-eyebrow text-[9px] uppercase tracking-[0.2em] text-amber-400 font-bold">Outstanding</p>
                    <p className="text-sm font-bold text-amber-400" data-testid={`monthly-outstanding-${k}`}>{gbp(m.outstanding)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
